import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { 
  getTenants, 
  getTenantLeases, 
  getOccupancyNotifications, 
  createOccupancyNotification, 
  getRentPayments,
  removeTenant,
  notifyTenantVacate,
  recordTenantPayment,
  getPendingUsers,
  approveUser,
  rejectUser,
  confirmRentReceipt,
  getRemovalHistory
} from '@/services/api';
import { useAuth } from '@/contexts/AuthContext';
import { 
  Users, 
  Home, 
  Clock, 
  CheckCircle2, 
  XCircle, 
  IndianRupee, 
  FileText, 
  UserPlus, 
  UserMinus,
  Trash2,
  Mail,
  Phone,
  AlertTriangle,
  CreditCard,
  Calendar,
  Copy,
  History
} from 'lucide-react';
import { toast } from 'sonner';

const ResidentTenants = () => {
  const { user } = useAuth();
  const [tenants, setTenants] = useState([]);
  const [leases, setLeases] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [rentPayments, setRentPayments] = useState([]);
  const [pendingTenants, setPendingTenants] = useState([]);
  const [pastTenants, setPastTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Dialog states
  const [addTenantOpen, setAddTenantOpen] = useState(false);
  const [vacateDialogOpen, setVacateDialogOpen] = useState(false);
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  // Form states
  const [tenantForm, setTenantForm] = useState({
    name: '',
    email: '',
    phone: '',
    monthly_rent: ''
  });
  const [paymentAmount, setPaymentAmount] = useState('');
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [selectedPendingTenant, setSelectedPendingTenant] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [vacancyReason, setVacancyReason] = useState('');
  const [removalReason, setRemovalReason] = useState('');

  const fetchData = async () => {
    try {
      setLoading(true);
      const [tenantsData, leasesData, notifsData, paymentsData, pendingData, pastTenantsData] = await Promise.all([
        getTenants(),
        getTenantLeases(),
        getOccupancyNotifications(),
        getRentPayments(),
        getPendingUsers().catch(() => []),
        getRemovalHistory('tenant', 50).catch(() => [])
      ]);
      setTenants(tenantsData.filter(t => t.role === 'tenant'));
      setLeases(leasesData);
      setNotifications(notifsData);
      setRentPayments(paymentsData);
      setPendingTenants(pendingData);
      setPastTenants(pastTenantsData);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Add new tenant request
  const handleAddTenant = async () => {
    if (!tenantForm.name || !tenantForm.email || !tenantForm.phone || !tenantForm.monthly_rent) {
      toast.error('Please fill all fields');
      return;
    }

    try {
      setSubmitting(true);
      await createOccupancyNotification({
        notification_type: 'tenant_occupy',
        tenant_details: {
          name: tenantForm.name,
          email: tenantForm.email,
          phone: tenantForm.phone,
          monthly_rent: parseFloat(tenantForm.monthly_rent)
        }
      });
      toast.success('Tenant request sent to admin for approval');
      setAddTenantOpen(false);
      setTenantForm({ name: '', email: '', phone: '', monthly_rent: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  // Notify admin about tenant vacancy
  const handleNotifyVacate = async () => {
    if (!selectedTenant) return;
    if (!vacancyReason.trim()) {
      toast.error('Please provide a reason for vacancy notification');
      return;
    }
    
    try {
      setSubmitting(true);
      await notifyTenantVacate(selectedTenant.id, vacancyReason.trim());
      toast.success('Vacancy notification sent to admin. Tenant status changed to pending.');
      setVacateDialogOpen(false);
      setSelectedTenant(null);
      setVacancyReason('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to notify');
    } finally {
      setSubmitting(false);
    }
  };

  // Remove tenant - request approval from admin
  
  const handleRemoveTenant = async () => {
    if (!selectedTenant) return;
    if (!removalReason.trim()) {
      toast.error('Please provide a reason for removal');
      return;
    }
    
    try {
      setSubmitting(true);
      const result = await removeTenant(selectedTenant.id, removalReason);
      toast.success(result.message || 'Removal request submitted');
      setRemoveDialogOpen(false);
      setSelectedTenant(null);
      setRemovalReason('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit removal request');
    } finally {
      setSubmitting(false);
    }
  };

  // Record payment on behalf of tenant
  const handleRecordPayment = async () => {
    if (!selectedTenant || !paymentAmount) {
      toast.error('Please enter payment amount');
      return;
    }
    
    try {
      setSubmitting(true);
      await recordTenantPayment(selectedTenant.id, parseFloat(paymentAmount));
      toast.success('Payment recorded successfully');
      setPaymentDialogOpen(false);
      setSelectedTenant(null);
      setPaymentAmount('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record payment');
    } finally {
      setSubmitting(false);
    }
  };

  // Confirm rent receipt from tenant
  const handleConfirmRentReceipt = async (paymentId) => {
    try {
      setSubmitting(true);
      await confirmRentReceipt(paymentId);
      toast.success('Rent payment confirmed!');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to confirm payment');
    } finally {
      setSubmitting(false);
    }
  };

  // Approve pending tenant registration
  const handleApprovePendingTenant = async (tenantId) => {
    try {
      setSubmitting(true);
      await approveUser(tenantId);
      toast.success('Tenant approved! Forwarded to admin for final approval.');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve tenant');
    } finally {
      setSubmitting(false);
    }
  };

  // Reject pending tenant registration
  const handleRejectPendingTenant = async () => {
    if (!selectedPendingTenant) return;
    if (!rejectionReason.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }
    
    try {
      setSubmitting(true);
      await rejectUser(selectedPendingTenant.id, rejectionReason);
      toast.success('Tenant registration rejected');
      setRejectDialogOpen(false);
      setSelectedPendingTenant(null);
      setRejectionReason('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject tenant');
    } finally {
      setSubmitting(false);
    }
  };

  const formatMonth = (monthStr) => {
    const [year, month] = monthStr.split('-');
    const date = new Date(year, parseInt(month) - 1);
    return date.toLocaleDateString('en-IN', { month: 'short', year: 'numeric' });
  };

  const getStatusBadge = (status) => {
    const variants = {
      pending: 'bg-amber-100 text-amber-800 border-amber-200',
      approved: 'bg-green-100 text-green-800 border-green-200',
      rejected: 'bg-red-100 text-red-800 border-red-200',
      paid: 'bg-green-100 text-green-800 border-green-200',
      active: 'bg-blue-100 text-blue-800 border-blue-200'
    };
    return variants[status] || variants.pending;
  };

  // Calculate stats - include all tenant statuses
  const activeTenants = tenants.filter(t => t.status === 'active');
  const pendingAdminTenants = tenants.filter(t => t.status === 'pending_admin');
  const pendingRemovalTenants = tenants.filter(t => t.status === 'pending_removal');
  const pendingVacancyTenants = tenants.filter(t => t.status === 'pending_vacancy');
  const allMyTenants = tenants; // All tenants (active, pending_admin, pending_removal, pending_vacancy)
  const activeLeases = leases.filter(l => l.status === 'active');
  const pendingRent = rentPayments.filter(p => ['pending', 'unpaid', 'submitted'].includes(p.status)).reduce((sum, p) => sum + p.amount, 0);
  const collectedRent = rentPayments.filter(p => p.status === 'paid').reduce((sum, p) => sum + p.amount, 0);
  const pendingRequests = notifications.filter(n => n.status === 'pending').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-secondary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="resident-tenants-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900">My Tenants</h1>
          <p className="text-slate-600 mt-1">Manage tenants for Unit {user?.unit_number}</p>
        </div>
        <Button onClick={() => setAddTenantOpen(true)} data-testid="add-tenant-btn">
          <UserPlus className="h-4 w-4 mr-2" />
          Add New Tenant
        </Button>
      </div>

      {/* Pending Tenant Approvals Alert */}
      {pendingTenants.length > 0 && (
        <Alert className="border-green-300 bg-green-50">
          <UserPlus className="h-5 w-5 text-green-600" />
          <AlertTitle className="text-green-800">New Tenant Registration(s)</AlertTitle>
          <AlertDescription className="text-green-700">
            {pendingTenants.length} tenant(s) waiting for your approval. Go to "Pending Approvals" tab.
          </AlertDescription>
        </Alert>
      )}

      {/* Rent Payment Submitted Alert */}
      {rentPayments.filter(p => p.status === 'submitted').length > 0 && (
        <Alert className="border-blue-300 bg-blue-50">
          <IndianRupee className="h-5 w-5 text-blue-600" />
          <AlertTitle className="text-blue-800">Rent Payment(s) Awaiting Confirmation</AlertTitle>
          <AlertDescription className="text-blue-700">
            {rentPayments.filter(p => p.status === 'submitted').length} tenant(s) have submitted rent payments. Go to "Rent Payments" tab to confirm receipt.
          </AlertDescription>
        </Alert>
      )}

      {/* Pending Requests Alert */}
      {pendingRequests > 0 && (
        <Alert className="border-amber-300 bg-amber-50">
          <Clock className="h-5 w-5 text-amber-600" />
          <AlertTitle className="text-amber-800">Pending Admin Requests</AlertTitle>
          <AlertDescription className="text-amber-700">
            You have {pendingRequests} request(s) waiting for admin approval.
          </AlertDescription>
        </Alert>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600">Active Tenants</p>
                <p className="text-2xl font-bold text-slate-900">{activeTenants.length}</p>
              </div>
              <Users className="h-8 w-8 text-secondary" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600">Active Leases</p>
                <p className="text-2xl font-bold text-slate-900">{activeLeases.length}</p>
              </div>
              <FileText className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-green-200 bg-green-50">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-green-600">Rent Collected</p>
                <p className="text-xl font-bold text-green-700 flex items-center gap-1">
                  <IndianRupee className="h-4 w-4" />
                  {collectedRent.toLocaleString('en-IN')}
                </p>
              </div>
              <CheckCircle2 className="h-8 w-8 text-green-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-amber-600">Pending Rent</p>
                <p className="text-xl font-bold text-amber-700 flex items-center gap-1">
                  <IndianRupee className="h-4 w-4" />
                  {pendingRent.toLocaleString('en-IN')}
                </p>
              </div>
              <Clock className="h-8 w-8 text-amber-400" />
            </div>
          </CardContent>
        </Card>
        <Card className={pendingRequests > 0 ? "border-purple-300 bg-purple-50" : ""}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600">Pending Requests</p>
                <p className="text-2xl font-bold text-slate-900">{pendingRequests}</p>
              </div>
              <AlertTriangle className={`h-8 w-8 ${pendingRequests > 0 ? 'text-purple-500' : 'text-slate-300'}`} />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue={pendingTenants.length > 0 ? "pending" : "tenants"} className="space-y-4">
        <TabsList>
          <TabsTrigger value="pending" data-testid="pending-tab" className={pendingTenants.length > 0 ? "text-green-600" : ""}>
            <UserPlus className="h-4 w-4 mr-2" />
            Pending Approvals {pendingTenants.length > 0 && `(${pendingTenants.length})`}
          </TabsTrigger>
          <TabsTrigger value="tenants" data-testid="tenants-tab">
            <Users className="h-4 w-4 mr-2" />
            Tenants ({activeTenants.length})
          </TabsTrigger>
          <TabsTrigger value="payments" data-testid="payments-tab">
            <CreditCard className="h-4 w-4 mr-2" />
            Rent Payments
          </TabsTrigger>
          <TabsTrigger value="requests" data-testid="requests-tab">
            <FileText className="h-4 w-4 mr-2" />
            My Requests
          </TabsTrigger>
          <TabsTrigger value="past" data-testid="past-tenants-tab" className="text-slate-500">
            <History className="h-4 w-4 mr-2" />
            Past Tenants {pastTenants.length > 0 && `(${pastTenants.length})`}
          </TabsTrigger>
        </TabsList>

        {/* Pending Approvals Tab */}
        <TabsContent value="pending">
          <Card>
            <CardHeader>
              <CardTitle>Pending Tenant Approvals</CardTitle>
              <CardDescription>Review and approve tenant registration requests</CardDescription>
            </CardHeader>
            <CardContent>
              {pendingTenants.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <CheckCircle2 className="h-12 w-12 mx-auto mb-3 text-green-300" />
                  <p className="font-medium">No pending approvals</p>
                  <p className="text-sm">When tenants register using your Resident Code, they'll appear here</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {pendingTenants.map((tenant) => (
                    <div 
                      key={tenant.id} 
                      className="p-4 bg-green-50 rounded-xl border border-green-200"
                      data-testid={`pending-tenant-${tenant.id}`}
                    >
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                        <div className="flex items-start gap-4">
                          <div className="h-12 w-12 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                            <UserPlus className="h-6 w-6 text-green-600" />
                          </div>
                          <div>
                            <p className="font-semibold text-slate-900">{tenant.name}</p>
                            <div className="flex flex-wrap items-center gap-3 text-sm text-slate-500 mt-1">
                              <span className="flex items-center gap-1">
                                <Mail className="h-3 w-3" />
                                {tenant.email}
                              </span>
                              <span className="flex items-center gap-1">
                                <Phone className="h-3 w-3" />
                                {tenant.phone}
                              </span>
                              <span className="flex items-center gap-1">
                                <Home className="h-3 w-3" />
                                Unit {tenant.unit_number}
                              </span>
                            </div>
                            {tenant.monthly_rent && (
                              <p className="text-sm text-slate-600 mt-1">
                                Proposed Rent: <span className="font-medium text-secondary">₹{Number(tenant.monthly_rent).toLocaleString('en-IN')}/month</span>
                              </p>
                            )}
                            <p className="text-xs text-slate-400 mt-2">
                              Registered: {new Date(tenant.created_at).toLocaleString('en-IN')}
                            </p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2 ml-16 md:ml-0">
                          <Badge className="bg-amber-100 text-amber-800 border-amber-200">
                            Awaiting Your Approval
                          </Badge>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-600 border-red-200 hover:bg-red-50"
                            onClick={() => {
                              setSelectedPendingTenant(tenant);
                              setRejectDialogOpen(true);
                            }}
                            disabled={submitting}
                            data-testid={`reject-pending-${tenant.id}`}
                          >
                            <XCircle className="h-4 w-4 mr-1" />
                            Reject
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => handleApprovePendingTenant(tenant.id)}
                            disabled={submitting}
                            data-testid={`approve-pending-${tenant.id}`}
                          >
                            <CheckCircle2 className="h-4 w-4 mr-1" />
                            Approve
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tenants Tab */}
        <TabsContent value="tenants">
          <Card>
            <CardHeader>
              <CardTitle>My Tenants</CardTitle>
              <CardDescription>Manage your tenants and track their status</CardDescription>
            </CardHeader>
            <CardContent>
              {allMyTenants.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <Users className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                  <p className="font-medium">No tenants</p>
                  <p className="text-sm">Click "Add New Tenant" to get started</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Pending Admin Approval Section */}
                  {pendingAdminTenants.length > 0 && (
                    <div className="mb-6">
                      <h3 className="text-sm font-semibold text-blue-700 mb-3 flex items-center gap-2">
                        <Clock className="h-4 w-4" />
                        Awaiting Admin Approval ({pendingAdminTenants.length})
                      </h3>
                      <div className="space-y-3">
                        {pendingAdminTenants.map((tenant) => (
                          <div 
                            key={tenant.id} 
                            className="p-4 bg-blue-50 rounded-xl border border-blue-200"
                            data-testid={`pending-admin-tenant-${tenant.id}`}
                          >
                            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                              <div className="flex items-start gap-4">
                                <div className="h-12 w-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                                  <Clock className="h-6 w-6 text-blue-600" />
                                </div>
                                <div>
                                  <p className="font-semibold text-slate-900">{tenant.name}</p>
                                  <div className="flex flex-wrap items-center gap-3 text-sm text-slate-500 mt-1">
                                    <span className="flex items-center gap-1"><Mail className="h-3 w-3" />{tenant.email}</span>
                                    <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{tenant.phone}</span>
                                    <span className="flex items-center gap-1"><Home className="h-3 w-3" />Unit {tenant.unit_number}</span>
                                  </div>
                                  {tenant.monthly_rent && (
                                    <p className="text-sm text-slate-600 mt-1">
                                      Rent: <span className="font-medium text-secondary">₹{Number(tenant.monthly_rent).toLocaleString('en-IN')}/month</span>
                                    </p>
                                  )}
                                </div>
                              </div>
                              <Badge className="bg-blue-100 text-blue-700 border-blue-200">
                                <Clock className="h-3 w-3 mr-1" />
                                Pending Admin Approval
                              </Badge>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Pending Removal Section */}
                  {pendingRemovalTenants.length > 0 && (
                    <div className="mb-6">
                      <h3 className="text-sm font-semibold text-amber-700 mb-3 flex items-center gap-2">
                        <AlertTriangle className="h-4 w-4" />
                        Pending Removal Approval ({pendingRemovalTenants.length})
                      </h3>
                      <div className="space-y-3">
                        {pendingRemovalTenants.map((tenant) => (
                          <div 
                            key={tenant.id} 
                            className="p-4 bg-amber-50 rounded-xl border border-amber-200"
                            data-testid={`pending-removal-tenant-${tenant.id}`}
                          >
                            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                              <div className="flex items-start gap-4">
                                <div className="h-12 w-12 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                                  <UserMinus className="h-6 w-6 text-amber-600" />
                                </div>
                                <div>
                                  <p className="font-semibold text-slate-900">{tenant.name}</p>
                                  <div className="flex flex-wrap items-center gap-3 text-sm text-slate-500 mt-1">
                                    <span className="flex items-center gap-1"><Mail className="h-3 w-3" />{tenant.email}</span>
                                    <span className="flex items-center gap-1"><Home className="h-3 w-3" />Unit {tenant.unit_number}</span>
                                  </div>
                                  <p className="text-sm text-amber-600 mt-2">
                                    <span className="font-medium">Removal Reason:</span> {tenant.removal_reason || 'Not specified'}
                                  </p>
                                </div>
                              </div>
                              <Badge className="bg-amber-100 text-amber-700 border-amber-200">
                                <Clock className="h-3 w-3 mr-1" />
                                Awaiting Admin Approval
                              </Badge>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Pending Vacancy Section */}
                  {pendingVacancyTenants.length > 0 && (
                    <div className="mb-6">
                      <h3 className="text-sm font-semibold text-orange-700 mb-3 flex items-center gap-2">
                        <Clock className="h-4 w-4" />
                        Pending Vacancy Approval ({pendingVacancyTenants.length})
                      </h3>
                      <div className="space-y-3">
                        {pendingVacancyTenants.map((tenant) => (
                          <div 
                            key={tenant.id} 
                            className="p-4 bg-orange-50 rounded-xl border border-orange-200"
                            data-testid={`pending-vacancy-tenant-${tenant.id}`}
                          >
                            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                              <div className="flex items-start gap-4">
                                <div className="h-12 w-12 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
                                  <Home className="h-6 w-6 text-orange-600" />
                                </div>
                                <div>
                                  <p className="font-semibold text-slate-900">{tenant.name}</p>
                                  <div className="flex flex-wrap items-center gap-3 text-sm text-slate-500 mt-1">
                                    <span className="flex items-center gap-1"><Mail className="h-3 w-3" />{tenant.email}</span>
                                    <span className="flex items-center gap-1"><Home className="h-3 w-3" />Unit {tenant.unit_number}</span>
                                  </div>
                                  <p className="text-sm text-orange-600 mt-2">
                                    <span className="font-medium">Vacancy Reason:</span> {tenant.vacancy_reason || 'Not specified'}
                                  </p>
                                  {tenant.vacancy_requested_by_name && (
                                    <p className="text-xs text-slate-500 mt-1">
                                      Requested by: {tenant.vacancy_requested_by_name}
                                    </p>
                                  )}
                                </div>
                              </div>
                              <Badge className="bg-orange-100 text-orange-700 border-orange-200">
                                <Clock className="h-3 w-3 mr-1" />
                                Awaiting Admin Approval
                              </Badge>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Active Tenants Section */}
                  {activeTenants.length > 0 && (
                    <div>
                      {(pendingAdminTenants.length > 0 || pendingRemovalTenants.length > 0 || pendingVacancyTenants.length > 0) && (
                        <h3 className="text-sm font-semibold text-green-700 mb-3 flex items-center gap-2">
                          <CheckCircle2 className="h-4 w-4" />
                          Active Tenants ({activeTenants.length})
                        </h3>
                      )}
                      <div className="space-y-3">
                        {activeTenants.map((tenant) => {
                          const lease = leases.find(l => l.tenant_id === tenant.id && l.status === 'active');
                          const tenantPayments = rentPayments.filter(p => p.tenant_id === tenant.id);
                          const currentMonth = new Date().toISOString().slice(0, 7);
                          // 'pending' is legacy; current records use 'unpaid'. 'submitted' = awaiting confirm.
                          const pendingPaymentsForTenant = tenantPayments.filter(p => ['pending', 'unpaid', 'submitted'].includes(p.status));
                          const pendingAmount = pendingPaymentsForTenant.reduce((sum, p) => sum + p.amount, 0);
                          const hasPaidThisMonth = tenantPayments.some(p => p.month === currentMonth && p.status === 'paid');
                          const submittedThisMonth = tenantPayments.some(p => p.month === currentMonth && p.status === 'submitted');
                          
                          return (
                            <div 
                              key={tenant.id} 
                              className="p-4 bg-slate-50 rounded-xl border border-slate-200"
                              data-testid={`tenant-card-${tenant.id}`}
                            >
                              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                                <div className="flex items-start gap-4">
                                  <div className="h-12 w-12 bg-secondary/10 rounded-full flex items-center justify-center flex-shrink-0">
                                    <Users className="h-6 w-6 text-secondary" />
                                  </div>
                                  <div>
                                    <p className="font-semibold text-slate-900">{tenant.name}</p>
                                    <div className="flex flex-wrap items-center gap-3 text-sm text-slate-500 mt-1">
                                      <span className="flex items-center gap-1"><Mail className="h-3 w-3" />{tenant.email}</span>
                                      <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{tenant.phone}</span>
                                      <span className="flex items-center gap-1"><Home className="h-3 w-3" />Unit {tenant.unit_number}</span>
                                    </div>
                                    {lease && (
                                      <div className="flex items-center gap-4 mt-2 text-sm">
                                        <span className="text-slate-600">
                                          Rent: <span className="font-medium text-secondary">₹{lease.monthly_rent?.toLocaleString('en-IN')}/month</span>
                                        </span>
                                        <span className="text-slate-600">
                                          Since: <span className="font-medium">{lease.lease_start}</span>
                                        </span>
                                      </div>
                                    )}
                                    {pendingAmount > 0 && (
                                      <p className="text-sm text-amber-600 mt-1 font-medium">
                                        ₹{pendingAmount.toLocaleString('en-IN')} rent pending
                                        {submittedThisMonth && <span className="ml-1 text-blue-600">(payment submitted, awaiting your confirmation)</span>}
                                      </p>
                                    )}
                                    {pendingAmount === 0 && hasPaidThisMonth && (
                                      <p className="text-sm text-green-600 mt-1 font-medium">
                                        Rent for this month is paid
                                      </p>
                                    )}
                                  </div>
                                </div>
                                
                                <div className="flex flex-wrap items-center gap-2 ml-16 md:ml-0">
                                  <Badge className={getStatusBadge('active')}>Active</Badge>
                                  
                                  {/* Record Payment Button — disabled if current month is already paid */}
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    disabled={hasPaidThisMonth}
                                    title={hasPaidThisMonth ? 'Rent for this month is already paid' : (submittedThisMonth ? 'Tenant has submitted payment — use Confirm Receipt instead, or override here' : '')}
                                    onClick={() => {
                                      setSelectedTenant(tenant);
                                      setPaymentAmount(lease?.monthly_rent?.toString() || '');
                                      setPaymentDialogOpen(true);
                                    }}
                                    data-testid={`record-payment-btn-${tenant.id}`}
                                  >
                                    <CreditCard className="h-4 w-4 mr-1" />
                                    Record Payment
                                  </Button>
                                  
                                  {/* Notify Vacancy Button */}
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="text-amber-600 border-amber-200 hover:bg-amber-50"
                                    onClick={() => {
                                      setSelectedTenant(tenant);
                                      setVacateDialogOpen(true);
                                    }}
                                    data-testid={`notify-vacate-btn-${tenant.id}`}
                                  >
                                    <UserMinus className="h-4 w-4 mr-1" />
                                    Notify Vacancy
                                  </Button>
                                  
                                  {/* Remove Tenant Button */}
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="text-red-600 border-red-200 hover:bg-red-50"
                                    onClick={() => {
                                      setSelectedTenant(tenant);
                                      setRemoveDialogOpen(true);
                                    }}
                                    data-testid={`remove-tenant-btn-${tenant.id}`}
                                  >
                                    <Trash2 className="h-4 w-4 mr-1" />
                                    Remove
                                  </Button>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {activeTenants.length === 0 && pendingAdminTenants.length === 0 && pendingRemovalTenants.length === 0 && (
                    <div className="text-center py-12 text-slate-500">
                      <Users className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                      <p className="font-medium">No tenants</p>
                      <p className="text-sm">Click "Add New Tenant" to get started</p>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Rent Payments Tab */}
        <TabsContent value="payments">
          <Card>
            <CardHeader>
              <CardTitle>Rent Payment History</CardTitle>
              <CardDescription>Track rent payments from your tenants</CardDescription>
            </CardHeader>
            <CardContent>
              {rentPayments.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <CreditCard className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                  <p className="font-medium">No rent payments yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {/* Submitted Payments (Need Confirmation) */}
                  {rentPayments.filter(p => p.status === 'submitted').length > 0 && (
                    <div className="mb-4">
                      <h3 className="text-sm font-semibold text-blue-700 mb-2">Awaiting Your Confirmation</h3>
                      {rentPayments.filter(p => p.status === 'submitted').map((payment) => (
                        <div 
                          key={payment.id} 
                          className="flex items-center justify-between p-4 bg-blue-50 rounded-xl border border-blue-200 mb-2"
                          data-testid={`submitted-payment-${payment.id}`}
                        >
                          <div className="flex items-center gap-4">
                            <div className="p-3 rounded-lg bg-blue-100">
                              <Clock className="h-5 w-5 text-blue-600" />
                            </div>
                            <div>
                              <p className="font-medium text-slate-900">{payment.tenant_name}</p>
                              <p className="text-sm text-slate-500">
                                {formatMonth(payment.month)} | Unit {payment.unit_number}
                              </p>
                              <p className="text-xs text-blue-600 mt-1">
                                Submitted: {payment.submitted_date || 'Today'}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="text-right">
                              <p className="font-semibold text-slate-900 flex items-center gap-1 justify-end">
                                <IndianRupee className="h-4 w-4" />
                                {payment.amount?.toLocaleString('en-IN')}
                              </p>
                            </div>
                            <Button
                              size="sm"
                              onClick={() => handleConfirmRentReceipt(payment.id)}
                              disabled={submitting}
                              className="bg-green-600 hover:bg-green-700"
                              data-testid={`confirm-payment-${payment.id}`}
                            >
                              <CheckCircle2 className="h-4 w-4 mr-1" />
                              Confirm Receipt
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* All Payments History */}
                  <h3 className="text-sm font-semibold text-slate-700 mb-2">Payment History</h3>
                  {rentPayments.map((payment) => (
                    <div 
                      key={payment.id} 
                      className={`flex items-center justify-between p-4 rounded-xl ${
                        payment.status === 'submitted' ? 'hidden' : 'bg-slate-50'
                      }`}
                      data-testid={`payment-row-${payment.id}`}
                    >
                      <div className="flex items-center gap-4">
                        <div className={`p-3 rounded-lg ${
                          payment.status === 'paid' ? 'bg-green-100' : 
                          payment.status === 'pending' ? 'bg-amber-100' : 'bg-slate-100'
                        }`}>
                          {payment.status === 'paid' ? (
                            <CheckCircle2 className="h-5 w-5 text-green-600" />
                          ) : (
                            <Clock className="h-5 w-5 text-amber-600" />
                          )}
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{payment.tenant_name}</p>
                          <p className="text-sm text-slate-500">
                            {formatMonth(payment.month)} | Unit {payment.unit_number}
                          </p>
                          {payment.status === 'paid' && (
                            <p className="text-xs text-green-600 mt-0.5">
                              {payment.confirmed_by_name
                                ? `Confirmed by ${payment.confirmed_by_name} on ${payment.confirmed_date}`
                                : payment.recorded_by_name
                                ? `Recorded by ${payment.recorded_by_name} on ${payment.paid_date}`
                                : payment.confirmed_date ? `Confirmed on ${payment.confirmed_date}` : ''
                              }
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="font-semibold text-slate-900 flex items-center gap-1 justify-end">
                            <IndianRupee className="h-4 w-4" />
                            {payment.amount?.toLocaleString('en-IN')}
                          </p>
                          {payment.paid_date && (
                            <p className="text-xs text-slate-500">Paid: {payment.paid_date}</p>
                          )}
                        </div>
                        <Badge className={getStatusBadge(payment.status)}>
                          {payment.status}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* My Requests Tab */}
        <TabsContent value="requests">
          <Card>
            <CardHeader>
              <CardTitle>My Requests</CardTitle>
              <CardDescription>Track the status of your tenant/occupancy requests</CardDescription>
            </CardHeader>
            <CardContent>
              {notifications.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <FileText className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                  <p className="font-medium">No requests submitted</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {notifications.map((notif) => (
                    <div 
                      key={notif.id} 
                      className="p-4 bg-slate-50 rounded-xl border border-slate-200"
                      data-testid={`request-${notif.id}`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          {notif.notification_type.includes('occupy') ? (
                            <UserPlus className="h-5 w-5 text-green-600" />
                          ) : (
                            <UserMinus className="h-5 w-5 text-red-600" />
                          )}
                          <span className="font-medium capitalize">
                            {notif.notification_type.replace(/_/g, ' ')}
                          </span>
                        </div>
                        <Badge className={getStatusBadge(notif.status)}>
                          {notif.status}
                        </Badge>
                      </div>
                      {notif.tenant_details && (
                        <p className="text-sm text-slate-600 mb-2">
                          Tenant: {notif.tenant_details.name} ({notif.tenant_details.email})
                        </p>
                      )}
                      {notif.admin_remarks && (
                        <p className="text-sm text-slate-500 bg-white p-2 rounded mt-2">
                          Admin: {notif.admin_remarks}
                        </p>
                      )}
                      {notif.tenant_key && notif.status === 'approved' && (
                        <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded">
                          <p className="text-sm text-green-700">
                            <strong>Tenant Key:</strong> <code className="bg-green-100 px-2 py-1 rounded">{notif.tenant_key}</code>
                          </p>
                          <p className="text-xs text-green-600 mt-1">
                            Share this with your tenant for their records. Default password: tenant123
                          </p>
                        </div>
                      )}
                      <p className="text-xs text-slate-400 mt-2">
                        Submitted: {new Date(notif.created_at).toLocaleString('en-IN')}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Past Tenants Tab */}
        <TabsContent value="past">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <History className="h-5 w-5 text-slate-600" />
                Past Tenants
              </CardTitle>
              <CardDescription>History of tenants who have been removed from your property</CardDescription>
            </CardHeader>
            <CardContent>
              {pastTenants.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <History className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                  <p className="font-medium">No past tenants</p>
                  <p className="text-sm">Removed tenants will appear here</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {pastTenants.map((tenant) => (
                    <div 
                      key={tenant.id} 
                      className="p-4 rounded-xl bg-slate-100 border border-slate-200"
                      data-testid={`past-tenant-${tenant.id}`}
                    >
                      <div className="flex flex-col md:flex-row md:items-start justify-between gap-3">
                        <div className="flex items-start gap-3">
                          <div className="h-10 w-10 rounded-full bg-slate-200 flex items-center justify-center">
                            <UserMinus className="h-5 w-5 text-slate-500" />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 flex-wrap mb-1">
                              <span className="font-semibold text-slate-900">{tenant.name}</span>
                              <Badge className="bg-slate-200 text-slate-600 border-slate-300">
                                Removed
                              </Badge>
                            </div>
                            
                            {/* Tenant Details */}
                            <div className="text-sm text-slate-600 space-y-1">
                              <p className="flex items-center gap-2">
                                <Mail className="h-3 w-3" />
                                {tenant.email}
                              </p>
                              {tenant.unit_number && (
                                <p className="flex items-center gap-2">
                                  <Home className="h-3 w-3" />
                                  Unit {tenant.unit_number}
                                </p>
                              )}
                              {tenant.phone && (
                                <p className="flex items-center gap-2">
                                  <Phone className="h-3 w-3" />
                                  {tenant.phone}
                                </p>
                              )}
                            </div>

                            {/* Removal Reason */}
                            {tenant.removal_reason && (
                              <div className="mt-3 p-3 bg-white rounded-lg border border-slate-200">
                                <p className="text-xs font-medium text-slate-600 mb-1 flex items-center gap-1">
                                  <AlertTriangle className="h-3 w-3" />
                                  Reason for Removal:
                                </p>
                                <p className="text-sm text-slate-700">{tenant.removal_reason}</p>
                              </div>
                            )}

                            {/* Removal Details */}
                            <div className="mt-2 text-xs text-slate-500 space-y-1">
                              {tenant.removal_requested_by_name && (
                                <p>Requested by: <span className="font-medium">{tenant.removal_requested_by_name}</span></p>
                              )}
                              {tenant.removal_approved_by_name && (
                                <p>Approved by: <span className="font-medium">{tenant.removal_approved_by_name}</span></p>
                              )}
                              {tenant.removed_by_name && !tenant.removal_approved_by_name && (
                                <p>Removed by: <span className="font-medium">{tenant.removed_by_name}</span></p>
                              )}
                              {tenant.vacancy_approved_by_name && (
                                <p>Vacancy approved by: <span className="font-medium">{tenant.vacancy_approved_by_name}</span></p>
                              )}
                              {tenant.vacancy_reason && !tenant.removal_reason && (
                                <div className="mt-1 p-2 bg-white rounded border border-slate-200">
                                  <p className="text-xs font-medium text-slate-600 mb-0.5">Vacancy Reason:</p>
                                  <p className="text-sm text-slate-700">{tenant.vacancy_reason}</p>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                        
                        <div className="text-xs text-slate-500 whitespace-nowrap flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {tenant.deactivated_at ? new Date(tenant.deactivated_at).toLocaleDateString('en-IN', { 
                            day: 'numeric',
                            month: 'short', 
                            year: 'numeric'
                          }) : 'N/A'}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add Tenant Dialog - Shows Resident Code */}
      <Dialog open={addTenantOpen} onOpenChange={setAddTenantOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add New Tenant</DialogTitle>
            <DialogDescription>
              Share your unique Resident Code with your tenant so they can register on FortiHome.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <Alert className="border-blue-200 bg-blue-50">
              <Home className="h-5 w-5 text-blue-600" />
              <AlertTitle className="text-blue-800">Your Resident Code</AlertTitle>
              <AlertDescription className="text-blue-700">
                <div className="mt-2 p-3 bg-white rounded-lg border border-blue-200 flex items-center justify-between">
                  <code className="text-2xl font-mono font-bold text-slate-900 tracking-wider" data-testid="resident-code-display">{user?.resident_code || 'Loading...'}</code>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => {
                      const text = user?.resident_code;
                      if (!text) return;
                      if (navigator.clipboard && navigator.clipboard.writeText) {
                        navigator.clipboard.writeText(text).then(() => {
                          toast.success('Resident Code copied to clipboard!');
                        }).catch(() => {
                          try {
                            const ta = document.createElement('textarea');
                            ta.value = text;
                            ta.style.position = 'fixed';
                            ta.style.opacity = '0';
                            document.body.appendChild(ta);
                            ta.select();
                            document.execCommand('copy');
                            document.body.removeChild(ta);
                            toast.success('Resident Code copied to clipboard!');
                          } catch {
                            toast.error('Copy failed. Code: ' + text);
                          }
                        });
                      } else {
                        try {
                          const ta = document.createElement('textarea');
                          ta.value = text;
                          ta.style.position = 'fixed';
                          ta.style.opacity = '0';
                          document.body.appendChild(ta);
                          ta.select();
                          document.execCommand('copy');
                          document.body.removeChild(ta);
                          toast.success('Resident Code copied to clipboard!');
                        } catch {
                          toast.error('Copy failed. Code: ' + text);
                        }
                      }
                    }}
                    data-testid="copy-resident-code-btn"
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </AlertDescription>
            </Alert>
            
            <div className="text-sm text-slate-600 space-y-2">
              <p className="font-medium">How it works:</p>
              <ol className="list-decimal list-inside space-y-1 text-slate-500">
                <li>Share this Resident Code with your tenant</li>
                <li>Tenant signs up on FortiHome using this code</li>
                <li>You'll see their request in "Pending Approvals" below</li>
                <li>After you approve, Admin gives final approval</li>
              </ol>
            </div>

            <Alert className="border-amber-200 bg-amber-50">
              <AlertTriangle className="h-5 w-5 text-amber-600" />
              <AlertDescription className="text-amber-700 text-sm">
                Your Unit: <strong>{user?.unit_number}</strong> — Only share this code with tenants renting your unit.
              </AlertDescription>
            </Alert>
          </div>
          <DialogFooter>
            <Button onClick={() => setAddTenantOpen(false)} data-testid="close-landlord-id-dialog">
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Notify Vacancy Dialog */}
      <Dialog open={vacateDialogOpen} onOpenChange={(open) => { setVacateDialogOpen(open); if (!open) setVacancyReason(''); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Notify Tenant Vacancy</DialogTitle>
            <DialogDescription>
              Notify admin that {selectedTenant?.name} will be vacating. Admin approval is required.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <Alert className="border-orange-200 bg-orange-50">
              <AlertTriangle className="h-5 w-5 text-orange-600" />
              <AlertTitle className="text-orange-800">Admin Approval Required</AlertTitle>
              <AlertDescription className="text-orange-700">
                This will change the tenant's status to "Pending Vacancy". The admin will review and approve or reject the vacancy request.
              </AlertDescription>
            </Alert>
            <div>
              <Label htmlFor="vacancy-reason">Reason for Vacancy *</Label>
              <textarea
                id="vacancy-reason"
                className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 mt-2"
                placeholder="Please provide a reason for vacancy (e.g., end of lease, tenant relocating, mutual agreement)..."
                value={vacancyReason}
                onChange={(e) => setVacancyReason(e.target.value)}
                data-testid="vacancy-reason-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setVacateDialogOpen(false); setVacancyReason(''); }}>Cancel</Button>
            <Button 
              onClick={handleNotifyVacate} 
              disabled={submitting || !vacancyReason.trim()}
              className="bg-orange-600 hover:bg-orange-700"
              data-testid="confirm-vacate-btn"
            >
              {submitting ? 'Submitting...' : 'Submit Vacancy Request'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Remove Tenant Dialog */}
      <Dialog open={removeDialogOpen} onOpenChange={(open) => { setRemoveDialogOpen(open); if (!open) setRemovalReason(''); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Request Tenant Removal</DialogTitle>
            <DialogDescription>
              Submit a removal request for {selectedTenant?.name}. Admin approval is required.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <Alert className="border-amber-200 bg-amber-50">
              <AlertTriangle className="h-5 w-5 text-amber-600" />
              <AlertTitle className="text-amber-800">Admin Approval Required</AlertTitle>
              <AlertDescription className="text-amber-700">
                This request will be sent to the admin for approval. The tenant will not be able to login until a decision is made.
              </AlertDescription>
            </Alert>
            <div>
              <Label htmlFor="removal-reason">Reason for Removal *</Label>
              <textarea
                id="removal-reason"
                className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 mt-2"
                placeholder="Please provide a reason for removing this tenant (e.g., lease violation, non-payment, end of contract)..."
                value={removalReason}
                onChange={(e) => setRemovalReason(e.target.value)}
                data-testid="removal-reason-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setRemoveDialogOpen(false); setRemovalReason(''); }}>Cancel</Button>
            <Button 
              className="bg-amber-600 hover:bg-amber-700" 
              onClick={handleRemoveTenant} 
              disabled={submitting || !removalReason.trim()}
              data-testid="confirm-remove-btn"
            >
              {submitting ? 'Submitting...' : 'Submit Removal Request'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Record Payment Dialog */}
      <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Record Rent Payment</DialogTitle>
            <DialogDescription>
              Record a rent payment for {selectedTenant?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="payment-amount">Payment Amount (₹)</Label>
              <Input
                id="payment-amount"
                type="number"
                placeholder="15000"
                value={paymentAmount}
                onChange={(e) => setPaymentAmount(e.target.value)}
                data-testid="payment-amount-input"
              />
            </div>
            <p className="text-sm text-slate-500">
              This will record a payment for the current month. Use this if the tenant paid you directly (cash/transfer).
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPaymentDialogOpen(false)}>Cancel</Button>
            <Button 
              onClick={handleRecordPayment} 
              disabled={submitting}
              data-testid="confirm-payment-btn"
            >
              {submitting ? 'Recording...' : 'Record Payment'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Pending Tenant Dialog */}
      <Dialog open={rejectDialogOpen} onOpenChange={(open) => { setRejectDialogOpen(open); if (!open) setRejectionReason(''); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Tenant Registration</DialogTitle>
            <DialogDescription>
              Reject {selectedPendingTenant?.name}'s registration request?
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <Alert variant="destructive">
              <AlertTriangle className="h-5 w-5" />
              <AlertTitle>Warning</AlertTitle>
              <AlertDescription>
                This will permanently delete the tenant's registration. They will need to register again if they want to join.
              </AlertDescription>
            </Alert>
            <div>
              <Label htmlFor="rejection-reason">Reason for Rejection *</Label>
              <textarea
                id="rejection-reason"
                className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 mt-2"
                placeholder="Please provide a reason for rejecting this registration..."
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                data-testid="rejection-reason-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setRejectDialogOpen(false); setRejectionReason(''); }}>Cancel</Button>
            <Button 
              variant="destructive" 
              onClick={handleRejectPendingTenant} 
              disabled={submitting || !rejectionReason.trim()}
              data-testid="confirm-reject-btn"
            >
              {submitting ? 'Rejecting...' : 'Reject Registration'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ResidentTenants;
